import {
  ApartmentOutlined,
  BlockOutlined,
  CalendarOutlined,
  CommentOutlined,
  ContactsOutlined,
  FieldBinaryOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  TagOutlined,
  UserOutlined,
} from "@ant-design/icons"
import { Button, Checkbox, Col, DatePicker, Divider, Row, Space, Spin, Typography } from "antd"
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore"
import React, { useEffect, useRef, useState } from "react"
import { Notification } from "../../../components/Notification"
import Client from "../../../components/Orders/Client"
import LabelCount from "../../../components/Orders/LabelCount"
import Manufacturers from "../../../components/Orders/Manufacturers"
import Product from "../../../components/Orders/Product"
import Recipients from "../../../components/Orders/Recipients"
import SalesComment from "../../../components/Orders/SalesComment"
import Sidebar from "../../../components/Orders/Sidebar"
import { db } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { useNavigate } from "react-router-dom"
import ChangableSpace from "../../../components/Orders/ChangableSpace"
import moment from "moment/moment"
import TapeCount from "../../../components/Orders/TapeCount"
import NewJob from "../../../components/Orders/NewJob"
import ProductTypeEnforcer from "./Helper/ProductTypeEnforcer"
import { updateProductOrdersArray } from "../../../utils/updateProductOrdersArray"

const { Title, Text, Paragraph } = Typography

//style for columns
const colStyle = { background: "white", borderRadius: 10, padding: 25 }

export default function CreateOrder({
  fromDrawer,
  drawerProduct,
  drawerManufacturer,
  drawerClient,
  drawerLoading,
}) {
  //gets user data
  const { user } = useAuthContext()

  //navigation from react router dom
  const navigate = useNavigate()
  const productTypeEnforcerRef = useRef(null)

  //selected manufacturer
  const [selectedManufacturer, setSelectedManufacturer] = useState(null)
  //selected client
  const [selectedClient, setSelectedClient] = useState(null)
  // selected product
  const [selectedProduct, setSelectedProduct] = useState(null)
  // selected label count
  const [labelCount, setSelectedLabelCount] = useState({})
  // requires pre-payment
  const [reqPrePayment, setReqPrePayment] = useState(false)
  const [orderCost, setOrderCost] = useState(0)

  //recipient
  const [selectedRecipient, setSelectedRecipient] = useState(null)
  //sales comment
  const [salesComment, setSalesComment] = useState(null)
  //are there any null keys?
  const [nullKeys, setNullKeys] = useState(false)
  //disables the create order button
  const [buttonDisable, setButtonDisable] = useState(true)

  //new job
  const [newJob, setNewJob] = useState(false)

  //changable space
  const [changableSpaceData, setChangableSpaceData] = useState(null)

  //order due
  const [orderDueDate, setOrderDueDate] = useState()

  //tape data
  const [selectedTapePrice, setSelectedTapePrice] = useState({})

  //loading from drawer
  //executes only if data is from drawer
  const [loadingFromDrawer, setLoadingFromDrawer] = useState(true)

  //products for
  const [productsFor, setProductsFor] = useState("priceCalculation")

  //price data for order
  const [newPriceWithParameters, setNewPriceWithParameters] = useState({
    cenaPar1000uzlimemEur: 0,
    totalCount: 0,
  })

  // State for manual proforma request
  const [manualProforma, setManualProforma] = useState(false)
  const [isDeniedClient, setIsDeniedClient] = useState(false)

  // Check for denied clients immediately when client is selected
  useEffect(() => {
    const checkIfDenied = async () => {
      if (!selectedClient) {
        setIsDeniedClient(false)
        return
      }

      try {
        const deniedClientsRef = collection(db, "denied_clients")
        const snapshot = await getDocs(deniedClientsRef)

        for (const deniedDoc of snapshot.docs) {
          const deniedClient = deniedDoc.data()

          if (
            (deniedClient.client && deniedClient.client.toLowerCase() === selectedClient.client?.toLowerCase()) ||
            (deniedClient.vat && deniedClient.vat.toLowerCase() === selectedClient.vat?.toLowerCase()) ||
            (deniedClient.reg && deniedClient.reg.toLowerCase() === selectedClient.reg?.toLowerCase())
          ) {
            setIsDeniedClient(true)
            return
          }
        }

        setIsDeniedClient(false)
      } catch (error) {
        console.error("Error checking denied clients:", error)
        setIsDeniedClient(false)
      }
    }

    checkIfDenied()
  }, [selectedClient])

  useEffect(() => {
    if (productsFor === "tapePrices") {
      const requiresProforma =
        selectedClient &&
        selectedTapePrice &&
        selectedTapePrice.price &&
        selectedTapePrice.rolls &&
        Number(selectedClient.creditLeft) <
          Number(selectedTapePrice.price) * Number(selectedTapePrice.rolls)

      const orderCost =
        selectedTapePrice && selectedTapePrice.price && selectedTapePrice.rolls
          ? Number(selectedTapePrice.price) * Number(selectedTapePrice.rolls)
          : 0

      setOrderCost(orderCost)
      setReqPrePayment(requiresProforma)
    } else {
      const requiresProforma =
        selectedClient &&
        labelCount &&
        labelCount.price &&
        labelCount.quantity &&
        Number(selectedClient.creditLeft) <
          Number(labelCount.price) * (Number(labelCount.quantity) / 1000)

      const orderCost =
        labelCount && labelCount.price && labelCount.quantity
          ? Number(labelCount.price) * (Number(labelCount.quantity) / 1000)
          : 0

      setOrderCost(orderCost)
      setReqPrePayment(requiresProforma)
    }
  }, [productsFor, selectedClient, labelCount, selectedTapePrice])

  //use effect only when the datais coming from the drawer order creation
  useEffect(() => {
    if (fromDrawer) {
      setLoadingFromDrawer(true)

      setSelectedManufacturer(drawerManufacturer)
      setSelectedClient(drawerClient)
      setSelectedProduct(drawerProduct)
      setLoadingFromDrawer(false)

      if (drawerProduct.machine === "Tape") {
        setProductsFor("tapePrices")
      } else {
        setProductsFor("priceCalculation")
      }
    }
    console.log(drawerManufacturer)
  }, [fromDrawer, drawerManufacturer, drawerClient, drawerProduct])

  const isEmptyObject = (obj) =>
    obj && typeof obj === "object" && !Array.isArray(obj) && Object.keys(obj).length === 0

  useEffect(() => {
    //returns early if data is loading
    if (fromDrawer && loadingFromDrawer) return

    const hasManufacturer = selectedManufacturer && !isEmptyObject(selectedManufacturer) // <-- new

    if (selectedProduct && selectedProduct.machine === "Tape") {
      if (!nullKeys && hasManufacturer && selectedClient && selectedProduct && selectedRecipient) {
        setButtonDisable(false)
      } else {
        setButtonDisable(true)
      }
    } else {
      if (
        !nullKeys &&
        hasManufacturer &&
        selectedClient &&
        selectedProduct &&
        Object.keys(labelCount).length !== 0 &&
        selectedRecipient
      ) {
        if (selectedProduct?.advanced?.changableSpace) {
          setButtonDisable(changableSpaceData === null)
        } else {
          setButtonDisable(false)
        }
      } else {
        setButtonDisable(true)
      }
    }
  }, [
    nullKeys,
    selectedClient,
    selectedProduct,
    labelCount,
    selectedRecipient,
    loadingFromDrawer,
    changableSpaceData,
    fromDrawer,
    selectedManufacturer,
  ])

  if (fromDrawer) {
    if (drawerLoading) {
      return (
        <Text>
          Loading... <Spin />
        </Text>
      )
    }
  }

  //creates order
  //creates order
  const createOrderCore = async () => {
    // Check if client is on denied list
    if (isDeniedClient) {
      return
    }

    // Get the reference to the orderSequence document
    const orderSequenceDocRef = doc(db, "sequence", "orderSequence")

    try {
      // Get the current order number
      const orderSequenceSnap = await getDoc(orderSequenceDocRef)
      if (!orderSequenceSnap.exists()) {
        throw new Error("Order sequence document does not exist")
      }

      const currentOrderNumber = orderSequenceSnap.data().number
      const orderPrefix = orderSequenceSnap.data().prefix

      // Increment the order number
      const newOrderNumber = currentOrderNumber + 1

      // Global order object with default orderStatus of 1 (normal order)
      const rootObject = {
        fullOrder: true,
        manufacturer: selectedManufacturer,
        uid: selectedClient.user,
        user: selectedClient.userEmail,
        paymentStatus: 0,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        updatedBy: user.uid,
        client: selectedClient,
        selectedProduct: selectedProduct,
        clientId: selectedClient.id,
        orderDueDate: orderDueDate,
        recipient: selectedRecipient,
        salesComment: salesComment,
        hasRoll: false,
        hasNewKnife: selectedProduct.knifeId === "none" ? true : false,
        orderStatus: 1,
        proformaPaymentStatus: 0,
        invoicePaymentStatus: 0,
        hasInvoice: false,
        newJob: newJob,
        changableSpaceData: changableSpaceData,
        userData: {
          designer: "CkTVS255TUcDK3FJnt9PiNDG4yL2",
          designerEmail: "aldis@pprintgroup.com",
          salesManager: user.uid,
          salesEmail: user.email,
        },
        justOrderNumber: newOrderNumber,
        orderNumber: orderPrefix + newOrderNumber,
        orderNumberPrefix: orderPrefix,
      }

      let newObject

      //if the machine is tape
      if (selectedProduct.machine === "Tape") {
        newObject = {
          ...rootObject,
          rollCount: selectedTapePrice.rolls,
          price: selectedTapePrice.price,
          boxCount: selectedTapePrice.boxCount,
          orderType: "Tape",
        }
      } else {
        //if the machine is for printing
        newObject = {
          ...rootObject,
          labelCount: labelCount.quantity,
          price: labelCount.price,
          weight: labelCount.labelWeight,
          orderType: "Label",
        }
      }

      // // If insufficient credit, mark order as requiring proforma
      if (reqPrePayment || manualProforma) {
        newObject.orderStatus = 0.5
      }

      // Skip designer step if product type is Sagatave or machine is Cutter
      if (selectedProduct.productType === "Sagatave" || selectedProduct.machine === "Cutter") {
        newObject.orderStatus = 2
      }

      // Get the reference to the orders collection
      const ref = collection(db, "orders")

      // Add the new order to the database
      await addDoc(ref, newObject)

      // Update the order sequence document with the new order number
      await updateDoc(orderSequenceDocRef, {
        number: newOrderNumber,
        lastUpdate: serverTimestamp(),
        updatedBy: user.uid,
        updatedByEmail: user.email,
        completeName: orderPrefix + newOrderNumber,
      })

      // Update the selected client document to reflect credit used:
      // Always increase creditUsed by orderCost, and decrease creditLeft by orderCost
      const clientDocRef = doc(db, "clients", selectedClient.id)
      const currentCreditUsed = selectedClient.creditUsed ? Number(selectedClient.creditUsed) : 0
      const currentCreditLeft = Number(selectedClient.creditLeft)
      await updateDoc(clientDocRef, {
        creditUsed: currentCreditUsed + orderCost,
        creditLeft: currentCreditLeft - orderCost,
      })

      await updateProductOrdersArray("create", newObject)

      // Show success notification
      Notification("success", "Order Created", "Your order has been created!")

      // Navigate to the all orders page
      navigate("/orders/all-orders/")
    } catch (error) {
      // Handle any errors that occur during the order creation process
      Notification("error", "Error Creating Order", error.message)
    }
  }

  const handleCreateClick = async (e) => {
    e.preventDefault()
    try {
      // Ensure the product has productType (opens modal only if needed)
      const ensured = await productTypeEnforcerRef.current.ensure(selectedProduct)
      if (ensured && ensured !== selectedProduct) {
        setSelectedProduct(ensured)
      }
      await createOrderCore()
    } catch (err) {
      // user cancelled or update failed — do nothing or show a toast if you want
      // Notification("info", "Cancelled", "Order creation was cancelled.")
    }
  }

  //displayin content for changable space
  const changableSpaceContent = () => {
    if (
      selectedProduct &&
      selectedProduct.machine !== "Tape" &&
      selectedProduct?.advanced?.changableSpace
    ) {
      return (
        <>
          <Col span={24}>
            <div style={{ backgroundColor: "#ffa39e", padding: 15, borderRadius: 15 }}>
              <Space align="center">
                <BlockOutlined />
                <Text>Changable Space</Text>
              </Space>
              <Paragraph style={{ fontSize: 12 }} type="secondary">
                There is a changable space inside your product, please provide the information about
                it.
              </Paragraph>
              <ChangableSpace setChangableSpaceData={setChangableSpaceData} />
            </div>
          </Col>
        </>
      )
    }
  }

  //date handle

  const handleDateChange = (date) => {
    // Convert the moment object to a JavaScript Date
    const jsDate = date.toDate()

    // Convert the JavaScript Date to a Firebase Timestamp
    const firebaseTimestamp = Timestamp.fromDate(jsDate)

    setOrderDueDate(firebaseTimestamp)
  }

  //disables the date before yesterday
  const disabledDate = (current) => {
    // Can not select days before yesterday (including yesterday)
    return current && current < moment().subtract(1, "days").endOf("day")
  }

  //returns the correct count selecter based on machine
  const returnCorrectCountSelect = () => {
    if (selectedProduct && selectedProduct.machine === "Tape") {
      return (
        <TapeCount
          setSelectedTapePrice={setSelectedTapePrice}
          selectedTapePrice={selectedTapePrice}
          tapeRolls={selectedProduct.prices}
        />
      )
    } else {
      return (
        <>
          <LabelCount
            newPriceWithParameters={newPriceWithParameters}
            setNewPriceWithParameters={setNewPriceWithParameters}
            value={labelCount}
            setProduct={setSelectedProduct}
            product={selectedProduct}
            setValue={setSelectedLabelCount}
            drawerLoading={drawerLoading}
            buttonSize={"middle"}
          />
        </>
      )
    }
  }

  return (
    <div style={{ padding: 15 }}>
      <Title level={3}>
        <PlusCircleOutlined />
        <Divider type="vertical" />
        Create Order
      </Title>
      {/* MAIN ROW */}
      <Row gutter={[15, 15]}>
        {/* CONTENT COLUMN */}
        <Col span={fromDrawer ? 24 : 16}>
          <div style={colStyle}>
            <Row gutter={[10, 25]}>
              {/* MANUFACTURER */}
              <Col span={12}>
                <Space align="center">
                  <ApartmentOutlined />
                  <Text>Select manufacturer</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Here you can choose the manufacturer
                </Paragraph>
                <Manufacturers value={selectedManufacturer} setValue={setSelectedManufacturer} />
              </Col>
              {/* CLIENT */}
              <Col span={12}>
                <Space align="center">
                  <UserOutlined />
                  <Text>Select Client</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Here you have to select the desired client
                </Paragraph>
                <Client
                  value={selectedClient}
                  nullKeys={nullKeys}
                  setNullKeys={setNullKeys}
                  user={user && user}
                  setValue={setSelectedClient}
                />
              </Col>
              {/* PRODUCT */}
              <Col span={12}>
                <Space align="center">
                  <TagOutlined />
                  <Text>Select Product</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Select your product
                </Paragraph>
                <Product
                  setNewPriceWithParameters={setNewPriceWithParameters}
                  value={selectedProduct}
                  client={selectedClient}
                  setValue={setSelectedProduct}
                  productsFor={productsFor}
                  setProductsFor={setProductsFor}
                />
              </Col>
              {/* AMOUNT */}
              <Col span={12}>
                <Space align="center">
                  <FieldBinaryOutlined />
                  <Text>
                    {selectedProduct && selectedProduct.machine === "Tape"
                      ? "Roll Count"
                      : "Label Count"}
                  </Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Select amount for{" "}
                  {selectedProduct && selectedProduct.machine === "Tape" ? "rolls" : "labels"}
                </Paragraph>
                {returnCorrectCountSelect()}
              </Col>
              {/* DUE DATE */}
              <Col span={12}>
                <Space align="center">
                  <CalendarOutlined />
                  <Text>Due Date</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Select due date (Min 1 week)
                </Paragraph>
                <DatePicker
                  onChange={handleDateChange}
                  format="DD/MM/YYYY"
                  disabledDate={disabledDate}
                  style={{ width: "100%" }}
                />
              </Col>
              {/* RECIPIENT */}
              <Col span={12}>
                <Space align="center">
                  <ContactsOutlined />
                  <Text>Recipient</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Who is gonna receive the order?
                </Paragraph>
                <Recipients value={selectedRecipient} setValue={setSelectedRecipient} />
              </Col>
              {/* NEW JOB */}
              <Col span={12}>
                <Space align="center">
                  <QuestionCircleOutlined />
                  <Text>New Job</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Is this going to be a new job?
                </Paragraph>
                <NewJob value={newJob} setValue={setNewJob} />
              </Col>

              {/* MANUAL PROFORMA REQUEST CHECKBOX */}
              <Col span={12}>
                <Space align="center">
                  <FileTextOutlined />
                  <Text>Request Proforma</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  Manually request a proforma for this order.
                </Paragraph>
                <Checkbox
                  checked={manualProforma}
                  onChange={(e) => setManualProforma(e.target.checked)}>
                  Request Proforma Invoice
                </Checkbox>
              </Col>

              {/* CHANGABLE SPACE */}
              {/* CHECKS IF THERE IS A CHANGABLE SPACE */}
              {changableSpaceContent()}

              {/* SALES COMMENT */}
              <Col span={24}>
                <Space align="center">
                  <CommentOutlined />
                  <Text>Comment</Text>
                </Space>
                <Paragraph style={{ fontSize: 12 }} type="secondary">
                  You can add extra comment for the order
                </Paragraph>
                <SalesComment value={salesComment} setValue={setSalesComment} />
              </Col>
              {reqPrePayment && (
                <Col span={24}>
                  <Title level={4} type="danger">
                    This will require proforma in order to start the order
                  </Title>
                  <Paragraph type="danger">
                    Your order costs {orderCost.toFixed(2)} EUR, but your available credit is{" "}
                    {Number(selectedClient.creditLeft).toFixed(2)} EUR.
                  </Paragraph>
                </Col>
              )}

              {/* CREATE ORDER BUTTON */}
              <Col span={24}>
                <ProductTypeEnforcer
                  ref={productTypeEnforcerRef}
                  user={user}
                  product={selectedProduct}
                />
                {isDeniedClient && (
                  <Text
                    type="danger"
                    strong
                    style={{
                      display: "block",
                      marginBottom: 12,
                      fontSize: 14,
                    }}>
                    ⚠️ This client is on the denied list. You cannot create orders for them.
                  </Text>
                )}
                <Button
                  onClick={handleCreateClick}
                  disabled={buttonDisable || isDeniedClient}
                  type="primary"
                  block>
                  {/* {requiresProforma ? "Create Order & Proforma" :  */}
                  Create Order
                  {/* } */}
                </Button>
              </Col>
            </Row>
          </div>
        </Col>
        {/* SIDEBAR */}
        {fromDrawer ? (
          ""
        ) : (
          <Col span={8}>
            <div style={colStyle}>
              <Title level={4}>Order Details</Title>
              <Sidebar
                selectedManufacturer={selectedManufacturer}
                selectedClient={selectedClient}
                selectedProduct={selectedProduct}
                labelCount={labelCount}
                selectedRecipient={selectedRecipient}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  )
}
